import { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { recipesApi }    from '@/api/recipes';
import { ingredientsApi } from '@/api/ingredients';
import { useToast }      from '@/providers/ToastProvider';
import {
  PageHeader, Button, FormField, Input, Textarea, Select, Card,
  CardHeader, CardTitle, CardContent, Spinner,
} from '@/components/ui';
import { ArrowLeft, Plus, Trash2, Thermometer } from 'lucide-react';
import { formatCurrency, formatPct } from '@/lib/utils';
import type { RecipeCalculationResult } from '@/types';
import { useState } from 'react';

// ─── Zod schema ────────────────────────────────────────────────────────────────
const lineSchema = z.object({
  ingredient_id:  z.string().min(1, 'required'),
  grams_per_kilo: z.coerce.number().min(0, 'must be ≥ 0'),
  waste_ratio:    z.coerce.number().min(0).max(1, 'must be 0–1'),
  sort_order:     z.coerce.number().int(),
});

const formSchema = z.object({
  name:                 z.string().min(1, 'required'),
  recipe_type_id:       z.string().nullable().optional(),
  portion_weight_g:     z.coerce.number().positive('must be > 0'),
  multiplier:           z.coerce.number().positive('must be > 0'),
  overrun_pct:          z.coerce.number().min(0),
  tax_rate:             z.coerce.number().min(0).max(1),
  desired_margin:       z.coerce.number().min(0).max(0.9999),
  selling_price:        z.preprocess(v => (v === '' || v == null ? null : Number(v)), z.number().positive().nullable()),
  prep_time_min:        z.preprocess(v => (v === '' || v == null ? null : Number(v)), z.number().int().nonnegative().nullable()),
  cook_time_min:        z.preprocess(v => (v === '' || v == null ? null : Number(v)), z.number().int().nonnegative().nullable()),
  preparation_notes:    z.string().nullable().optional(),
  presentation_notes:   z.string().nullable().optional(),
  kitchen_notes:        z.string().nullable().optional(),
  ingredients:          z.array(lineSchema).min(1, 'add at least one ingredient'),
});

type FormValues = z.infer<typeof formSchema>;

// ─── Component ─────────────────────────────────────────────────────────────────
export default function RecipeFormPage() {
  const { t }          = useTranslation();
  const { id }         = useParams<{ id: string }>();
  const navigate        = useNavigate();
  const queryClient     = useQueryClient();
  const { toast }  = useToast();
  const isEdit          = !!id;

  // ── Remote data ──────────────────────────────────────────────────────────────
  const { data: existing, isLoading: loadingRecipe } = useQuery({
    queryKey: ['recipe', id],
    queryFn:  () => recipesApi.get(id!),
    enabled:  isEdit,
  });

  const { data: ingredientOptions = [] } = useQuery({
    queryKey: ['ingredients-dropdown'],
    queryFn:  ingredientsApi.dropdown,
  });

  const { data: recipeTypes = [] } = useQuery({
    queryKey: ['recipe-types'],
    queryFn:  recipesApi.listTypes,
  });

  // ── RHF setup ────────────────────────────────────────────────────────────────
  const {
    register, control, handleSubmit, reset, watch, getValues,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '', recipe_type_id: null,
      portion_weight_g: 100, multiplier: 1, overrun_pct: 0,
      tax_rate: 0.10, desired_margin: 0.35, selling_price: null,
      prep_time_min: null, cook_time_min: null,
      preparation_notes: '', presentation_notes: '', kitchen_notes: '',
      ingredients: [],
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'ingredients' });

  // Populate form when editing
  useEffect(() => {
    if (existing) {
      reset({
        name:               existing.name,
        recipe_type_id:     existing.recipe_type_id ?? null,
        portion_weight_g:   Number(existing.portion_weight_g),
        multiplier:         Number(existing.multiplier),
        overrun_pct:        Number(existing.overrun_pct),
        tax_rate:           Number(existing.tax_rate),
        desired_margin:     Number(existing.desired_margin),
        selling_price:      existing.selling_price != null ? Number(existing.selling_price) : null,
        prep_time_min:      existing.prep_time_min ?? null,
        cook_time_min:      existing.cook_time_min ?? null,
        preparation_notes:  existing.preparation_notes ?? '',
        presentation_notes: existing.presentation_notes ?? '',
        kitchen_notes:      existing.kitchen_notes ?? '',
        ingredients: existing.ingredients.map((l, i) => ({
          ingredient_id:  l.ingredient_id,
          grams_per_kilo: Number(l.grams_per_kilo),
          waste_ratio:    Number(l.waste_ratio),
          sort_order:     l.sort_order ?? i,
        })),
      });
    }
  }, [existing, reset]);

  // ── Live preview calculation ──────────────────────────────────────────────────
  const [preview, setPreview] = useState<RecipeCalculationResult | null>(
    existing?.calculations ?? null
  );

  const watchedLines = watch('ingredients');
  const watchPortion = watch('portion_weight_g');
  const watchMult    = watch('multiplier');
  const watchOverrun = watch('overrun_pct');
  const watchTax     = watch('tax_rate');
  const watchMargin  = watch('desired_margin');
  const watchPrice   = watch('selling_price');

  // Re-calculate whenever something relevant changes
  const [calcLoading, setCalcLoading] = useState(false);
  useEffect(() => {
    const lines = watchedLines;
    if (!lines || lines.length === 0) { setPreview(null); return; }
    const allFilled = lines.every(l => l.ingredient_id && l.grams_per_kilo >= 0);
    if (!allFilled) return;

    setCalcLoading(true);
    const payload = {
      portion_weight_g:  watchPortion,
      multiplier:        watchMult,
      overrun_pct:       watchOverrun,
      tax_rate:          watchTax,
      desired_margin:    watchMargin,
      selling_price:     watchPrice ?? undefined,
      ingredients:       lines.map(l => ({
        ingredient_id:  l.ingredient_id,
        grams_per_kilo: l.grams_per_kilo,
        waste_ratio:    l.waste_ratio,
      })),
    };
    recipesApi.preview(payload)
      .then(calc => setPreview(calc))
      .catch(() => {/* ignore preview errors */})
      .finally(() => setCalcLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    JSON.stringify(watchedLines),
    watchPortion, watchMult, watchOverrun, watchTax, watchMargin, watchPrice,
  ]);

  // ── Mutations ────────────────────────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: recipesApi.create,
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
      toast(t('recipes.created'), 'success');
      navigate(`/recipes/${r.id}`);
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: FormValues }) => recipesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
      queryClient.invalidateQueries({ queryKey: ['recipe', id] });
      toast(t('recipes.updated'), 'success');
      navigate(`/recipes/${id}`);
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const onSubmit = (data: FormValues) => {
    if (isEdit) {
      updateMutation.mutate({ id: id!, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const addLine = () =>
    append({ ingredient_id: '', grams_per_kilo: 0, waste_ratio: 0, sort_order: fields.length });

  // ── Loading ───────────────────────────────────────────────────────────────────
  if (isEdit && loadingRecipe) return <Spinner className="py-20" />;

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div>
      <PageHeader
        title={isEdit ? t('recipes.editRecipe') : t('recipes.newRecipe')}
        subtitle={existing?.name}
      >
        <Button asChild variant="outline" size="sm">
          <Link to={isEdit ? `/recipes/${id}` : '/recipes'}><ArrowLeft size={15} /> {t('common.back')}</Link>
        </Button>
      </PageHeader>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">

          {/* ── Main form column ────────────────────────────────────────────── */}
          <div className="space-y-6 lg:col-span-2">

            {/* Identity */}
            <Card>
              <CardHeader><CardTitle>{t('common.details')}</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <FormField label={t('common.name')} error={errors.name?.message} required>
                    <Input {...register('name')} placeholder={t('recipes.namePlaceholder')} />
                  </FormField>
                </div>
                <FormField label={t('recipes.type')} error={(errors.recipe_type_id as any)?.message}>
                  <Controller
                    name="recipe_type_id"
                    control={control}
                    render={({ field }) => (
                      <Select
                        value={field.value ?? ''}
                        onChange={e => field.onChange(e.target.value || null)}
                      >
                        <option value="">{t('common.selectOptional')}</option>
                        {recipeTypes.map(rt => (
                          <option key={rt.id} value={rt.id}>{rt.name}</option>
                        ))}
                      </Select>
                    )}
                  />
                </FormField>
                <FormField label={t('recipes.portionWeight')} error={errors.portion_weight_g?.message} required>
                  <Input type="number" step="0.01" {...register('portion_weight_g')} placeholder="100" />
                </FormField>
                <FormField label={t('recipes.multiplier')} error={errors.multiplier?.message} required>
                  <Input type="number" step="0.1" {...register('multiplier')} placeholder="1" />
                </FormField>
                <FormField label={t('recipes.overrunPct')} error={errors.overrun_pct?.message} required>
                  <Input type="number" step="0.1" {...register('overrun_pct')} placeholder="0" />
                </FormField>
                <FormField label={t('recipes.prepTime')} error={errors.prep_time_min?.message}>
                  <Input type="number" step="1" {...register('prep_time_min')} placeholder="—" />
                </FormField>
                <FormField label={t('recipes.cookTime')} error={errors.cook_time_min?.message}>
                  <Input type="number" step="1" {...register('cook_time_min')} placeholder="—" />
                </FormField>
              </CardContent>
            </Card>

            {/* Pricing */}
            <Card>
              <CardHeader><CardTitle>{t('recipes.pricing')}</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <FormField label={t('recipes.taxRate')} error={errors.tax_rate?.message} required>
                  <Input type="number" step="0.01" min="0" max="1" {...register('tax_rate')} placeholder="0.10" />
                </FormField>
                <FormField label={t('recipes.desiredMargin')} error={errors.desired_margin?.message} required>
                  <Input type="number" step="0.01" min="0" max="0.9999" {...register('desired_margin')} placeholder="0.35" />
                </FormField>
                <FormField label={t('recipes.sellingPrice')} error={(errors.selling_price as any)?.message}>
                  <Input type="number" step="0.01" {...register('selling_price')} placeholder="—" />
                </FormField>
              </CardContent>
            </Card>

            {/* Ingredient lines */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>{t('recipes.ingredients')}</CardTitle>
                  <Button type="button" size="sm" variant="outline" onClick={addLine}>
                    <Plus size={14} /> {t('recipes.addIngredient')}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {errors.ingredients?.root && (
                  <p className="text-sm text-destructive">{errors.ingredients.root.message}</p>
                )}
                {/* Column headers */}
                {fields.length > 0 && (
                  <div className="hidden sm:grid sm:grid-cols-[1fr_120px_100px_36px] sm:gap-2 px-1 text-xs text-muted-foreground font-semibold uppercase tracking-wide">
                    <span>{t('ingredients.name')}</span>
                    <span>{t('recipes.gramsPerKilo')}</span>
                    <span>{t('recipes.wasteRatio')}</span>
                    <span />
                  </div>
                )}

                {fields.map((field, idx) => (
                  <div
                    key={field.id}
                    className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_120px_100px_36px] sm:items-start border rounded-md p-2"
                  >
                    {/* Ingredient selector */}
                    <Controller
                      name={`ingredients.${idx}.ingredient_id`}
                      control={control}
                      render={({ field: f }) => (
                        <div>
                          <Select
                            value={f.value}
                            onChange={f.onChange}
                            className={errors.ingredients?.[idx]?.ingredient_id ? 'border-destructive' : ''}
                          >
                            <option value="">{t('ingredients.select')}</option>
                            {ingredientOptions.map(opt => (
                              <option key={opt.id} value={opt.id}>{opt.name}</option>
                            ))}
                          </Select>
                          {errors.ingredients?.[idx]?.ingredient_id && (
                            <p className="mt-1 text-xs text-destructive">{errors.ingredients[idx]!.ingredient_id?.message}</p>
                          )}
                        </div>
                      )}
                    />

                    {/* Grams per kilo */}
                    <div>
                      <Controller
                        name={`ingredients.${idx}.grams_per_kilo`}
                        control={control}
                        render={({ field: f }) => (
                          <Input
                            type="number" step="0.01" min="0"
                            placeholder="0"
                            value={f.value}
                            onChange={f.onChange}
                            className={errors.ingredients?.[idx]?.grams_per_kilo ? 'border-destructive' : ''}
                          />
                        )}
                      />
                      {errors.ingredients?.[idx]?.grams_per_kilo && (
                        <p className="mt-1 text-xs text-destructive">{errors.ingredients[idx]!.grams_per_kilo?.message}</p>
                      )}
                    </div>

                    {/* Waste ratio */}
                    <div>
                      <Controller
                        name={`ingredients.${idx}.waste_ratio`}
                        control={control}
                        render={({ field: f }) => (
                          <Input
                            type="number" step="0.001" min="0" max="1"
                            placeholder="0"
                            value={f.value}
                            onChange={f.onChange}
                          />
                        )}
                      />
                    </div>

                    {/* Remove */}
                    <Button
                      type="button" variant="ghost" size="icon"
                      className="text-destructive hover:text-destructive"
                      onClick={() => remove(idx)}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                ))}

                {fields.length === 0 && (
                  <p className="py-6 text-center text-sm text-muted-foreground">
                    {t('recipes.noIngredients')} — <button type="button" className="underline text-primary" onClick={addLine}>{t('recipes.addFirst')}</button>
                  </p>
                )}

                {/* Recipe base total */}
                {fields.length > 0 && (() => {
                  const vals = getValues('ingredients');
                  const total = vals.reduce((s, l) => s + (Number(l.grams_per_kilo) || 0), 0);
                  const diff  = Math.abs(total - 1000);
                  return (
                    <div className={`flex items-center justify-between text-sm rounded px-3 py-1.5 ${diff < 1 ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
                      <span>{t('recipes.recipeBase')}</span>
                      <span className="font-mono font-semibold">{total.toFixed(2)} g / 1000 g</span>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>

            {/* Notes */}
            <Card>
              <CardHeader><CardTitle>Notes</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <FormField label={t('recipes.preparationNotes')}>
                  <Textarea rows={3} {...register('preparation_notes')} placeholder={t('recipes.preparationNotesHint')} />
                </FormField>
                <FormField label={t('recipes.presentationNotes')}>
                  <Textarea rows={3} {...register('presentation_notes')} />
                </FormField>
                <FormField label={t('recipes.kitchenNotes')}>
                  <Textarea rows={2} {...register('kitchen_notes')} placeholder={t('recipes.kitchenNotesHint')} />
                </FormField>
              </CardContent>
            </Card>
          </div>

          {/* ── Calculation summary sidebar ─────────────────────────────────── */}
          <div className="space-y-4">
            <Card className="sticky top-4">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>{t('recipes.livePreview')}</CardTitle>
                  {calcLoading && <Spinner size="sm" />}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {preview ? (
                  <>
                    {/* Production */}
                    <Section title={t('recipes.production')}>
                      <Row label={t('recipes.totalProduction')} value={`${preview.total_production_grams.toFixed(0)} g`} />
                      <Row label={t('recipes.portions')}         value={String(preview.number_of_portions)} />
                    </Section>

                    {/* Serving temp */}
                    <div className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ${
                      preview.suggested_serving_temp === 'PAC ERROR'
                        ? 'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300'
                        : 'bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300'
                    }`}>
                      <Thermometer size={16} />
                      <span>{t('recipes.servingTemp')}:</span>
                      <span className="ml-auto font-bold">{preview.suggested_serving_temp}</span>
                    </div>

                    {/* Technical balance */}
                    <Section title={t('recipes.technicals')}>
                      <Row label={t('recipes.totalSugars')}  value={formatPct(preview.total_sugars_pct)} warn={preview.total_sugars_pct > 26} />
                      <Row label={t('recipes.totalFat')}     value={formatPct(preview.total_fat_pct)} />
                      <Row label={t('recipes.totalSML')}     value={formatPct(preview.total_sml_pct)} />
                      <Row label="Solids (non-fat)"          value={formatPct(preview.total_solids_diff)} />
                      <Row label={t('recipes.totalSolids')}  value={formatPct(preview.total_solids_total)} />
                      <Row label={t('recipes.totalPOD')}     value={String(preview.total_pod)} />
                      <Row label={t('recipes.totalPAC')}     value={String(preview.total_pac)} bold />
                    </Section>

                    {/* Financial */}
                    <Section title={t('recipes.financial')}>
                      <Row label={t('recipes.costPer1000g')}   value={formatCurrency(preview.total_cost_per_1000g)} />
                      <Row label={t('recipes.costPerPortion')} value={formatCurrency(preview.cost_per_portion)} bold />
                      <Row label={t('recipes.recommendedPrice')} value={formatCurrency(preview.recommended_price)} />
                      {watchPrice && (
                        <>
                          <Row label={t('recipes.actualMargin')} value={formatPct(preview.actual_margin_pct)} />
                        </>
                      )}
                    </Section>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-6">
                    {fields.length === 0 ? t('recipes.addIngredientToPreview') : t('recipes.fillIngredients')}
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Submit */}
            <Button type="submit" className="w-full" disabled={isSubmitting || createMutation.isPending || updateMutation.isPending}>
              {isSubmitting || createMutation.isPending || updateMutation.isPending
                ? t('common.saving')
                : isEdit ? t('common.saveChanges') : t('recipes.createRecipe')}
            </Button>
            <Button type="button" variant="outline" className="w-full" asChild>
              <Link to={isEdit ? `/recipes/${id}` : '/recipes'}>{t('common.cancel')}</Link>
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}

// ─── Small helper components ──────────────────────────────────────────────────
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</p>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Row({ label, value, bold, warn }: { label: string; value: string; bold?: boolean; warn?: boolean }) {
  return (
    <div className={`flex items-center justify-between text-sm ${warn ? 'text-amber-600' : ''}`}>
      <span className="text-muted-foreground">{label}</span>
      <span className={bold ? 'font-semibold' : ''}>{value}</span>
    </div>
  );
}
